import { FC, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

import styles from './styles.module.css';
import { Icon } from 'src/components/Icon';

const contactEmail = 'contact@playwall.co';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type LinkProps = {
  text: string;
};

const paragraphs = [
  'We, Kezerk Innovations Sdn Bhd, respect your privacy and are committed to protecting your personal data. In compliance with the Personal Data Protection Act, 2010 ("PDPA Act") and its regulations, we seek your consent to be given to us to collect, use, process, transfer and/or retain your personal data.',
  'We may collect and process your personal data in your participation in the interactive experience presented by Kezerk Innovations Sdn Bhd ("Client").',
  'The personal data which has been or will be collected and processed includes but is not limited to your personal details submitted by you through the QR Code, which may include name, age, correspondence address, email address, contact details, photograph, image, and voice, any sensitive information which may include information relating to your race, religious or similar belief; and/or other information about you from which you are identifiable.',
  'We will collect and process your personal data for purposes including but not limited to (i) generating a digital avatar of you, (ii) downloading the photograph together with the digital avatar and (iii) other purposes that are necessary or related to your usage of the digital avatar.',
  'Your personal data will be disclosed, disseminated and/or transferred to our affiliates, the Client and its affiliates, and any such third party authorised by or under any law or by order of a court, whether located within or outside Malaysia for any of the above purposes or any other purpose for which your personal data to be disclosed at the time of its collection. You hereby consent to us transferring your personal data outside of Malaysia for such purposes. Please note that other than what we have declared, we do not process or deal with your personal data in any other manner, and we take no responsibility for how your personal data is handled by the other party(ies).',
  'We will take all reasonable action to ensure the confidentiality, protection, security and accuracy of your personal data made available to us.',
  'It is necessary for us to collect and retain your personal data. If you do not provide us with your personal data or do not consent to this Notice, we are unable to generate your digital avatar. We shall retain your personal data for 1 month to fulfil the above purposes. Your personal data will be disposed of appropriately when we do not have to retain them.',
  'You confirm that we shall be entitled to use, free of charge, your photographs and/or recordings (without having to identify you by name), in their original format or edited, adapted or altered, for internal and external promotional and publicity materials and for any programmes, publications, websites, electronic publications and social media services worldwide produced by or on our behalf.',
  'You shall waive any right to royalties or other compensation arising from or related to the use of your photographs and/or recordings.',
];

const TermsAndConditionsModal: FC<ModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.modal}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="terms-and-conditions-title"
      >
        <div className={styles.header}>
          <h3 id="terms-and-conditions-title" className={styles.title}>
            Personal Data Protection Policy
          </h3>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close policy modal"
          >
            <Icon className={styles.closeIcon} name="close" />
          </button>
        </div>

        <div className={styles.content}>
          {paragraphs.map((paragraph, index) => (
            <p key={index} className={styles.paragraph}>
              {paragraph}
            </p>
          ))}

          <p className={styles.noMarginBottom}>
            You may access and request for correction/deletion (if your personal
            data is inaccurate, outdated, incomplete, etc) or limit the
            processing of your personal data and contact us with any enquiries
            or complaints in respect of your personal data at{' '}
            <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

const TermsAndConditionsLink: FC<LinkProps> = ({ text }) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  return (
    <>
      <button
        type="button"
        className={styles.openButton}
        onClick={() => setIsOpen(true)}
      >
        {text}
      </button>

      <TermsAndConditionsModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};

export { TermsAndConditionsModal, TermsAndConditionsLink };
